/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/libs/react-query.ts
 * @Description: React Query 配置文件
 *
 * 此文件用于配置 @tanstack/react-query 的 QueryClient
 * 提供了默认的配置选项，包括缓存时间、重试策略等
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * 创建并配置 QueryClient 实例
 *
 * 默认配置说明：
 * - defaultOptions.queries: 查询的默认配置
 *   - staleTime: 5分钟，数据在5分钟内被认为是新鲜的，不会重新获取
 *   - cacheTime: 10分钟，未使用的查询数据在内存中保留10分钟
 *   - retry: 失败时重试1次
 *   - refetchOnWindowFocus: 窗口获得焦点时不自动重新获取
 *   - refetchOnReconnect: 网络重连时自动重新获取
 *
 * - defaultOptions.mutations: 变更的默认配置
 *   - retry: 失败时不重试（变更操作通常不需要重试）
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 数据在5分钟内被认为是新鲜的，不会重新获取
      // staleTime: 5 * 60 * 1000, // 5分钟
      // 未使用的查询数据在内存中保留10分钟
      // cacheTime: 10 * 60 * 1000, // 10分钟
      // 失败时重试1次
      retry: false,
      // 窗口获得焦点时不自动重新获取（避免频繁请求）
      refetchOnWindowFocus: false,
      // 网络重连时自动重新获取
      refetchOnReconnect: true,
      // 挂载时不自动重新获取（如果数据已存在）
      refetchOnMount: false,
    },
    mutations: {
      // 变更操作失败时不重试
      retry: false,
    },
  },
});

/**
 * 查询键（Query Keys）工厂函数
 * 用于统一管理所有的查询键，避免硬编码字符串
 *
 * 使用示例：
 * ```typescript
 * // 用户相关查询键
 * queryKeys.user.detail(userId)
 * queryKeys.user.list()
 *
 * // 商品相关查询键
 * queryKeys.product.detail(productId)
 * queryKeys.product.list({ category: 'electronics' })
 * ```
 */
export const queryKeys = {
  // 用户相关查询键
  user: {
    // 获取用户详情
    detail: (userId?: string | number) => ['user', 'detail', userId] as const,
    // 获取用户列表
    list: (filters?: Record<string, any>) => ['user', 'list', filters] as const,
    // 获取用户配置
    config: () => ['user', 'config'] as const,
  },
  // 商品相关查询键
  product: {
    // 获取商品详情
    detail: (productId: string | number) => ['product', 'detail', productId] as const,
    // 获取商品列表
    list: (params?: Record<string, any>) => ['product', 'list', params] as const,
    // 获取商品分类
    categories: () => ['product', 'categories'] as const,
  },
  // 订单相关查询键
  order: {
    // 获取订单详情
    detail: (orderId: string | number) => ['order', 'detail', orderId] as const,
    // 获取订单列表
    list: (params?: Record<string, any>) => ['order', 'list', params] as const,
  },
  // 购物车相关查询键
  cart: {
    // 获取购物车列表
    list: () => ['cart', 'list'] as const,
    // 获取购物车数量
    count: () => ['cart', 'count'] as const,
  },
  // 商品分类相关查询键
  category: {
    // 获取商品分类列表
    list: () => ['category', 'list'] as const,
  },
} as const;
